import {
  useDrawerContext,
  useHeaderContext,
  useSnackbarContext,
} from "@WahooFitness/wahoo-offline-mfe";
import { useWorkoutProfilesContext } from "@/hooks/useWorkoutProfilesContext";
import { OverflowMenuHorizontal } from "@carbon/icons-react";
import { IconButton, ListItemIcon, Menu, MenuItem, Typography, useTheme } from "@mui/material";
import { MouseEvent, useCallback, useMemo, useRef, useState, KeyboardEvent } from "react";
import { Trans, t } from "@lingui/macro";
import { useNavigate } from "react-router";
import {
  WfIconContentArrowArcLeftRegular,
  WfIconContentDeleteRegular,
  WfIconContentDuplicateRegular,
  WfIconContentEditRegular,
} from "@WahooFitness/redesignr";
import useEditWorkoutProfile from "./useEditWorkoutProfile";
import { getWorkoutTypeNames } from "../AddWorkoutProfile/WorkoutTypeNames";
import { WahooWorkoutType } from "@WahooFitness/cloud-client-types";
import DeleteWorkoutProfileDrawer from "./Drawers/DeleteWorkoutProfileDrawer";
import ResetWorkoutProfileDrawer from "./Drawers/ResetWorkoutProfileDrawer";
import DuplicateWorkoutProfileDrawer from "./Drawers/DuplicateWorkoutProfileDrawer";
import RenameWorkoutProfileDrawer from "./Drawers/RenameWorkoutProfileDrawer";
interface Props {
  profileId: string;
  permanent?: boolean;
}

const ActionMenu = ({ profileId, permanent }: Props) => {
  const navigate = useNavigate();
  const { navigateBack } = useHeaderContext();
  const { enqueueSnackbar } = useSnackbarContext();
  const { deleteProfile, copyProfile, resetProfile, getWorkoutProfile } =
    useWorkoutProfilesContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const profileNameRef = useRef<HTMLInputElement>(null);
  const duplicateProfileNameRef = useRef<HTMLInputElement>(null);

  const handleCopy = useCallback(async () => {
    const newProfile = await copyProfile(profileId, duplicateProfileNameRef?.current?.value);
    if (newProfile) {
      navigate(`/workout-profiles/${newProfile.external_id}`);
    }
  }, [profileId, copyProfile, navigate]);

  const profile = useMemo(() => getWorkoutProfile(profileId), [getWorkoutProfile, profileId]);
  const { handleNameChange } = useEditWorkoutProfile(profile);

  const [nameError, setNameError] = useState<string>();
  const { workoutProfileNames } = useWorkoutProfilesContext();
  const { setDrawer, handleClose: handleCloseDrawer } = useDrawerContext();
  const workoutTypeNames = getWorkoutTypeNames();
  const workoutTypeName =
    profile?.workout_type_id !== undefined
      ? workoutTypeNames[WahooWorkoutType[profile?.workout_type_id]]
      : "";

  const checkValid = useCallback(
    (value: string | undefined) => {
      const newValue = value?.trim();
      if (!newValue) {
        setNameError(t`profile name cannot be blank`);
      } else if (
        profile?.name !== newValue &&
        workoutProfileNames
          .map((name) => name.toLocaleLowerCase())
          .includes(newValue.toLocaleLowerCase())
      ) {
        setNameError(t`name already in use`);
      } else {
        setNameError(undefined);
      }
    },
    [profile?.name, workoutProfileNames]
  );

  const handleNameSave = useCallback(() => {
    const newValue = profileNameRef.current?.value.trim();
    if (!nameError && newValue) {
      handleNameChange(newValue);
    }
  }, [handleNameChange, nameError]);

  const handleKeyPress = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter") {
        handleNameSave();
      }
      if (event.key === "Escape" && profileNameRef.current && profile?.name) {
        profileNameRef.current.value = profile.name;
        checkValid(profileNameRef.current.value);
      }
    },
    [handleNameSave, checkValid, profile?.name]
  );

  const handleDelete = useCallback(async () => {
    handleClose();
    setDrawer({
      hidePuller: true,
      hideDivider: true,
      open: true,
      hideHeader: true,
      children: (
        <DeleteWorkoutProfileDrawer
          profileName={profile?.name}
          handleCloseDrawer={handleCloseDrawer}
          handleDelete={async () => {
            await deleteProfile(profileId);
            navigateBack();
            handleCloseDrawer();
          }}
        />
      ),
    });
  }, [deleteProfile, handleCloseDrawer, navigateBack, profile?.name, profileId, setDrawer]);

  const handleReset = useCallback(async () => {
    handleClose();
    setDrawer({
      hideDivider: true,
      hideHeader: true,
      hidePuller: true,
      open: true,
      children: (
        <ResetWorkoutProfileDrawer
          workoutTypeName={workoutTypeName}
          profileName={profile?.name}
          handleCloseDrawer={handleCloseDrawer}
          handleReset={async () => {
            await resetProfile(profileId);
            handleCloseDrawer();
            enqueueSnackbar({
              message: t`${profile?.name} reset to default`,
              enableClickAway: true,
            });
          }}
        />
      ),
    });
  }, [
    setDrawer,
    profile?.name,
    workoutTypeName,
    handleCloseDrawer,
    resetProfile,
    profileId,
    enqueueSnackbar,
  ]);

  const handleDuplicate = useCallback(async () => {
    handleClose();
    setDrawer({
      hidePuller: true,
      hideDivider: true,
      open: true,
      title: t`Duplicate workout profile`,
      children: (
        <DuplicateWorkoutProfileDrawer
          handleKeyPress={handleKeyPress}
          profileName={profile?.name}
          error={nameError}
          checkValid={() => checkValid(duplicateProfileNameRef.current?.value)}
          duplicateProfileNameRef={duplicateProfileNameRef}
          handleSave={async () => {
            await handleCopy();
            handleCloseDrawer();
          }}
          handleCancel={() => {
            if (duplicateProfileNameRef.current) {
              duplicateProfileNameRef.current.value = profile?.name ?? "";
            }
            handleCloseDrawer();
          }}
        />
      ),
    });
  }, [
    setDrawer,
    handleKeyPress,
    profile?.name,
    nameError,
    checkValid,
    handleCopy,
    handleCloseDrawer,
    duplicateProfileNameRef,
  ]);

  const handleRename = useCallback(() => {
    handleClose();
    setDrawer({
      open: true,
      title: t`Rename workout profile`,
      hidePuller: true,
      hideDivider: true,
      elevation: 4,
      children: (
        <RenameWorkoutProfileDrawer
          profileName={profile?.name}
          profileNameRef={profileNameRef}
          handleCancel={() => {
            if (profileNameRef.current) {
              profileNameRef.current.value = profile?.name ?? "";
            }
            handleCloseDrawer();
          }}
          handleSave={() => {
            handleNameSave();
            handleCloseDrawer();
          }}
          handleKeyPress={handleKeyPress}
          error={nameError}
          checkValid={() => checkValid(profileNameRef.current?.value)}
        />
      ),
    });
  }, [
    setDrawer,
    profile?.name,
    checkValid,
    handleKeyPress,
    nameError,
    handleCloseDrawer,
    handleNameSave,
    profileNameRef,
  ]);

  const theme = useTheme();

  return (
    <>
      <IconButton
        id="basic-button"
        edge="end"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        size="large"
      >
        <OverflowMenuHorizontal size={24} />
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem onClick={handleRename}>
          <ListItemIcon>
            <WfIconContentEditRegular />
          </ListItemIcon>
          <Trans>Rename</Trans>
        </MenuItem>
        <MenuItem onClick={handleDuplicate}>
          <ListItemIcon>
            <WfIconContentDuplicateRegular />
          </ListItemIcon>
          <Trans>Duplicate</Trans>
        </MenuItem>
        <MenuItem onClick={handleReset}>
          <ListItemIcon>
            <WfIconContentArrowArcLeftRegular />
          </ListItemIcon>
          <Trans>Reset to default</Trans>
        </MenuItem>
        {!permanent && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <WfIconContentDeleteRegular color="error" />
            </ListItemIcon>
            <Typography color={theme.palette.error.main} variant="prose-base">
              <Trans>Delete</Trans>
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default ActionMenu;
